import { AfterViewChecked, ChangeDetectorRef, Component, OnInit, inject, Signal, computed } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { SalesforceUniqueFieldTarget, TemplateCriteria, TemplateCriteriaConfig } from '@r1-compass-library/models';
import { AssetStateMessageHandlingService } from '@r1-compass-library/services/asset-state-message-handling.service';
import { filterNil } from 'ngxtension/filter-nil';
import { injectParams } from 'ngxtension/inject-params';
import { InstructionsComponent } from '@r1-compass-library/components';
import { ActivatedRoute } from '@angular/router';
import { TemplateCriteriaDefinitionForm } from '@r1-compass-library/models/template-criteria/template-criteria-form.model';
import { TemplateCriteriaStore } from '../store/template-criteria.store';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import {
    FormUnsavedChangesWarningEntity,
    noopFormSaveMutationResult,
} from '@r1-compass-library/guards/hasUnsavedChanges.guard';
import { AssetInvalidSaveWarningService } from '../../asset-invalid-save-warning.service';
import { SaveGuardStore } from '@r1-compass-library/guards/save-guard.store';
import { TemplateCriteriaFormMap, TemplateCriteriaValidationService } from '../template-criteria-validation.service';
import { TemplateCriteriaValidationStateService } from '../template-criteria-validation-state.service';
import { toSignal } from '@angular/core/rxjs-interop';
import {
    SyncValidationStateSection,
    SyncValidationStateService,
    ValidationService,
    ValidationStateService,
} from '@r1-compass-library/features/validation-sync';
import { TemplateCriteriaSectionName } from '@r1-compass-library/features/validation-sync';

@Component({
    selector: 'r1-compass-app-template-criteria-definition',
    imports: [
        InstructionsComponent,
        MatFormFieldModule,
        ReactiveFormsModule,
        MatInputModule,
        MatButtonModule,
        MatSelectModule,
    ],
    templateUrl: './template-criteria-definition.component.html',
    styleUrl: './template-criteria-definition.component.scss',
})
export class TemplateCriteriaDefinitionComponent
    implements
        OnInit,
        AfterViewChecked,
        FormUnsavedChangesWarningEntity,
        SyncValidationStateSection<TemplateCriteriaSectionName, TemplateCriteria>
{
    readonly #store = inject(TemplateCriteriaStore);
    readonly #route = inject(ActivatedRoute);
    readonly #globalLoggedInUserStore = inject(GlobalLoggedInUserStore);
    readonly #assetStateMessageHandlingService = inject(AssetStateMessageHandlingService);
    readonly #changeDetectorRef = inject(ChangeDetectorRef);
    readonly #formSaveWarningStore = inject(SaveGuardStore);
    readonly #syncValidationStateService = inject(SyncValidationStateService);
    readonly #templateCriteriaValidationService = inject(TemplateCriteriaValidationService);
    readonly #templateCriteriaValidationStateService = inject(TemplateCriteriaValidationStateService);
    readonly #invalidSaveWarningService = inject(AssetInvalidSaveWarningService);

    templateId = injectParams('id');
    loggedInUserId: Signal<number | undefined> = this.#globalLoggedInUserStore.selectedUserId;

    salesforceTargetOptions =
        this.#globalLoggedInUserStore.selectedDivisionConfig()?.config?.mapping?.uniqueSalesforceTargetFields ?? [];

    definitionForm = new FormGroup<TemplateCriteriaDefinitionForm>(new TemplateCriteriaDefinitionForm());

    canDeactivate$ = this.#formSaveWarningStore.deriveCanDeactivate$(this.definitionForm);
    saveGuardHandlerFn = this.#formSaveWarningStore.formUnsavedChangesWarningEntityDefinition;

    formValue = toSignal(this.definitionForm.valueChanges);
    blockSave = computed<boolean>(() => !this.formValue()?.name); // Asset name is required by database table
    readonly allowPartialSave = true;

    private readonly canDeactivate = toSignal(this.canDeactivate$, { requireSync: true });

    public disableSave = computed<boolean>(
        () =>
            this.blockSave() ||
            this.#templateCriteriaValidationStateService.shouldDisableSave(
                this.#store.template(),
                this.canDeactivate(),
                this.#templateCriteriaValidationStateService.invalidSections(),
                'definition'
            )
    );

    constructor() {
        this.saveGuardHandlerFn({
            form: this.definitionForm,
            canDeactivate$: this.canDeactivate$,
            params: injectParams('id'),
            entity$: this.#store.template$,
            savedSoCanRoute$: this.#formSaveWarningStore.savedSoCanRoute$,
            route: this.#route,
            disableSave: this.disableSave,
        });
    }

    ngOnInit(): void {
        this.populateForm();
        this.definitionForm.markAllAsTouched(); // this makes the form indicate invalid fields on load

        this.subscribeToValidationState(
            this.definitionForm,
            'definition',
            this.#templateCriteriaValidationService,
            this.#templateCriteriaValidationStateService
        );

        this.#store.message$.pipe(filterNil()).subscribe(message => {
            this.#assetStateMessageHandlingService.handleMessage(message);
            this.#store.resetMessage();
        });
    }

    ngAfterViewChecked(): void {
        if (this.salesforceTargetOptions.length === 1) {
            const salesforceControl = this.definitionForm.controls.salesforceTargetId;
            salesforceControl.setValue(this.salesforceTargetOptions.at(0)?.id);
            salesforceControl.disable();
        }
        this.#changeDetectorRef.detectChanges();
    }

    private populateForm() {
        const templateId = this.templateId();
        if (templateId && templateId !== 'new') {
            this.#store.template$.pipe(filterNil()).subscribe(template => {
                this.definitionForm.patchValue(
                    new FormGroup<TemplateCriteriaDefinitionForm>(
                        new TemplateCriteriaDefinitionForm(template)
                    ).getRawValue()
                );
            });
            if (this.#store.template()?.id !== +templateId) {
                this.#store.getTemplate(+templateId);
            }
        } else {
            this.definitionForm.patchValue(
                new FormGroup<TemplateCriteriaDefinitionForm>(new TemplateCriteriaDefinitionForm()).getRawValue()
            );
        }
    }

    async onSave() {
        const existingTemplate = this.#store.template();
        const template: Partial<TemplateCriteria> = {
            ...existingTemplate,
            name: this.definitionForm.controls.name.value,
            description: this.definitionForm.controls.description.value,
            iamDivisionId: this.#globalLoggedInUserStore.selectedIamDivisionId(),
            updatedBy: this.loggedInUserId(),
            createdBy: this.createdBy(),
            valid: this.#templateCriteriaValidationStateService.invalidSections().length === 0,
        };
        const config: TemplateCriteriaConfig = {
            ...existingTemplate?.config,
            salesforceTarget: this.convertToSalesforceTarget(this.definitionForm.controls.salesforceTargetId.value),
        };
        if (this.templateId() === 'new') {
            template.folderId = this.checkForRootFolder();
        }
        if (!(await this.#invalidSaveWarningService.confirmSave('template criteria', template))) {
            return noopFormSaveMutationResult();
        }
        return this.#store.saveTemplateAndConfig({ template: template, config: config });
    }

    subscribeToValidationState(
        form: FormGroup<TemplateCriteriaDefinitionForm>,
        sectionName: TemplateCriteriaSectionName,
        validationService: ValidationService<TemplateCriteriaSectionName, TemplateCriteria, TemplateCriteriaFormMap>,
        validationStateService: ValidationStateService<TemplateCriteriaSectionName>
    ) {
        return this.#syncValidationStateService.subToValidationState(
            form,
            sectionName,
            validationService,
            validationStateService
        );
    }

    /**
     * Method for determining if asset is in Root folder or not.
     * Returns "undefined" if folderId is 0, as 0 is assumed to be Root.
     * Non-folder assets cannot have folderIds if they are created in Root, or else they will error due to database config.
     * @param id The current folderId
     * @private
     */
    private checkForRootFolder() {
        const id = this.#route.snapshot.queryParams ? Number(this.#route.snapshot.queryParams['folderId']) : 1;
        if (id === 0) {
            return undefined;
        } else {
            return id;
        }
    }

    private convertToSalesforceTarget(salesforceTargetId?: string): SalesforceUniqueFieldTarget | undefined {
        return this.salesforceTargetOptions.find(option => option.id === salesforceTargetId);
    }

    createdBy = computed(() => {
        return this.templateId() === 'new' ? this.loggedInUserId() : this.#store.template()?.createdBy;
    });
}
